import React from "react";
import { Link } from "react-router-dom";
import { Star, ArrowLeft, Check } from "lucide-react";
import SiteNavbar from "@/components/SiteNavbar";
import Footer from "@/components/Footer";
import Reveal from "@/components/Reveal";

const PACKAGES = [
  {
    name: "STARTER",
    price: "£150",
    cadence: "/month",
    popular: false,
    cta: "Get Started",
    features: [
      "Professional website hosting",
      "Monthly website updates and maintenance",
      "Security monitoring and backups",
      "Monthly performance report",
      "Email support",
      "12-month growth plan",
    ],
  },
  {
    name: "GROWTH",
    price: "£300",
    cadence: "/month",
    popular: true,
    cta: "Get Started",
    features: [
      "Everything in Starter",
      "Full SEO management and optimisation",
      "Google Business Profile management",
      "Monthly keyword ranking report",
      "Local search visibility campaigns",
      "Priority support",
      "12-month growth plan",
    ],
  },
  {
    name: "PRO",
    price: "From £500",
    cadence: "/month",
    popular: false,
    cta: "Book a Free Audit",
    features: [
      "Everything in Growth",
      "Meta ads management (Facebook and Instagram)",
      "Google ads management",
      "Monthly ad performance report",
      "Quarterly strategy calls",
      "Ad spend managed separately",
      "12-month growth plan",
    ],
  },
];

const FAQS = [
  {
    q: "Is ad spend included?",
    a: "No — ad spend goes directly to Meta or Google and is agreed separately.",
  },
  {
    q: "Why 12 months?",
    a: "Real results take time to build properly. Our 12-month plans give us the runway to deliver results that actually stick.",
  },
  {
    q: "Can I upgrade?",
    a: "Yes, you can move between plans at any time.",
  },
];

export default function Packages() {
  return (
    <div className="min-h-screen bg-[#0a1f3d] text-white">
      <SiteNavbar />

      <main className="mx-auto max-w-7xl px-5 py-14 sm:px-8">
        <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-sm text-white/80">
          <Star className="h-4 w-4 text-[#e9a31e]" />
          Pricing
        </div>

        <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-6xl">
          Simple, Transparent <span className="text-[#e9a31e]">Pricing</span>
        </h1>
        <p className="mt-4 max-w-3xl text-lg text-white/70">
          All packages run on a 12-month growth plan. Ad spend is always
          separate and goes directly to Meta or Google — it is never included in
          these fees.
        </p>

        <Reveal>
        <div className="mt-12 rounded-2xl border border-[#e9a31e]/30 bg-gradient-to-br from-[#0e2647] to-[#0a1f3d] p-8 sm:flex sm:items-center sm:justify-between sm:gap-6">
          <div>
            <h2 className="text-2xl font-extrabold sm:text-3xl">
              One-Off Build Fee
            </h2>
            <p className="mt-3 max-w-2xl text-white/75">
              All packages include a one-off website build fee tailored to your
              project. Get in touch for a quote.
            </p>
          </div>
          <Link
            to="/contact"
            className="mt-5 inline-flex shrink-0 rounded-lg bg-[#e9a31e] px-6 py-3.5 text-base font-bold text-[#0a1f3d] transition-colors hover:bg-[#f2b743] sm:mt-0"
          >
            Get a Quote
          </Link>
        </div>
        </Reveal>

        <Reveal>
        <div className="mt-6 grid items-start gap-6 lg:grid-cols-3">
          {PACKAGES.map((p) => (
            <article
              key={p.name}
              className={`relative flex flex-col rounded-2xl border p-8 transition-all duration-200 ${
                p.popular
                  ? "border-[#e9a31e] bg-white/[0.05] lg:-mt-4 lg:mb-4 shadow-2xl shadow-black/30"
                  : "border-white/10 bg-white/[0.03]"
              }`}
            >
              {p.popular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#e9a31e] px-4 py-1 text-xs font-bold uppercase tracking-wider text-[#0a1f3d]">
                  Most Popular
                </span>
              )}

              <h3 className="text-sm font-bold uppercase tracking-widest text-white/60">
                {p.name}
              </h3>
              <div className="mt-3 flex items-end gap-1">
                <span className="text-4xl font-extrabold text-white">
                  {p.price}
                </span>
                <span className="pb-1 text-white/55">{p.cadence}</span>
              </div>

              <ul className="mt-7 flex-1 space-y-3.5">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-white/80">
                    <Check className="mt-0.5 h-5 w-5 shrink-0 text-[#e9a31e]" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <Link
                to={p.cta === "Book a Free Audit" ? "/audit" : "/contact"}
                className={`mt-8 rounded-lg px-6 py-3.5 text-center text-base font-bold transition-colors ${
                  p.popular
                    ? "bg-[#e9a31e] text-[#0a1f3d] hover:bg-[#f2b743]"
                    : "border border-white/40 bg-white/5 text-white hover:border-white hover:bg-white/10"
                }`}
              >
                {p.cta}
              </Link>
            </article>
          ))}
        </div>
        </Reveal>

        <p className="mt-8 max-w-3xl text-sm text-white/55">
          All fees shown are management fees only. Ad spend budgets are agreed
          separately and paid directly to Meta or Google.
        </p>

        <p className="mt-4 max-w-3xl text-sm text-white/55">
          All packages include a one-off website build fee. Pricing is tailored
          to your project — get in touch for a quote.
        </p>
        <Link
          to="/contact"
          className="mt-5 inline-flex rounded-lg bg-[#e9a31e] px-6 py-3 text-sm font-bold text-[#0a1f3d] transition-colors hover:bg-[#f2b743]"
        >
          Get a Quote
        </Link>

        {/* FAQ */}
        <Reveal>
        <section className="mt-16">
          <h2 className="text-2xl font-extrabold sm:text-3xl">
            Frequently asked questions
          </h2>
          <div className="mt-6 space-y-4">
            {FAQS.map((item) => (
              <div
                key={item.q}
                className="rounded-xl border border-white/10 bg-white/[0.03] p-6"
              >
                <h3 className="text-lg font-bold text-white">{item.q}</h3>
                <p className="mt-2 text-white/70">{item.a}</p>
              </div>
            ))}
          </div>
        </section>
        </Reveal>
      </main>

      <Footer />
    </div>
  );
}
